/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import {
  Button,
  Backdrop,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import APIManager from "../../../Services/ApiManager/ApiManager";
import AddEcuTypeModal from "./AddEcuTypeModal";
import "./EcuType.css";
import PopupDialog from "../../../Components/PopupDialog";

const ROWS_PER_PAGE = 10;

const EcuType = () => {
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(null);
  const [ecuData, setEcuData] = useState([]);
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [addEcuTypeModalOpen, setAddEcuTypeModalOpen] = useState(false);

  const readOnly_DomainApprover =
    sessionStorage.getItem("readonly_DomainApprover") === "true";

  useEffect(() => {
    fetchEcuTypes();
  }, []);

  const fetchEcuTypes = async () => {
    try {
      setLoader(true);
      setError(null);
      const response = await APIManager.getGlobalAdminEcuTypes();

      if (response && Array.isArray(response)) {
        console.log("Fetched ECU Types Response:", response);
        const flattenedData = [];
        response.forEach((item) => {
          if (item.ecuTypes && Array.isArray(item.ecuTypes) && item.ecuTypes.length > 0) {
            item.ecuTypes.forEach((ecuType) => {
              flattenedData.push({
                id: `${item.make}-${ecuType || ""}`,
                make: item.make,
                ecuType: ecuType || "",
              });
            });
          } else {
            flattenedData.push({
              id: `${item.make}-empty`,
              make: item.make,
              ecuType: "",
            });
          }
        });

        setEcuData(flattenedData);
      } else {
        setEcuData([]);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setComponentName("appError");
      setErrorMessage(err?.message || "Failed to fetch ECU types");
      setHeaderMessage(
        err?.message === "jwt expired" ? "401 Token Expired" : "Error",
      );
      setOpenErrorPopup(true);
      setEcuData([]);
    } finally {
      setLoader(false);
    }
  };

  const handleSort = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const sortedData = [...ecuData].sort((a, b) => {
    const compareValue = a.ecuType.localeCompare(b.ecuType);
    return sortOrder === "asc" ? compareValue : -compareValue;
  });
  const totalPages = Math.ceil(sortedData.length / ROWS_PER_PAGE);
  const paginatedData = sortedData.slice(
    currentPage * ROWS_PER_PAGE,
    (currentPage + 1) * ROWS_PER_PAGE,
  );
 
  const handleDelete = (item) => {
    const deleteForm = {
      make: item.make,
      ecuType: item.ecuType,
    };
    const makeName = item.make.trim();
    const ecuType = item.ecuType;
    APIManager.deleteEcuTypeDomainApprover(deleteForm, makeName, ecuType)
      .then((response) => {
        fetchEcuTypes();
      })
      .catch((err) => {
        console.error("Error deleting ECU type:", err);
        setComponentName("appError");
        setErrorMessage(err.message || "Failed to delete ECU type");
        setHeaderMessage(
          err.message === "jwt expired" ? "401 Token Expired" : "Error",
        );
        setOpenErrorPopup(true);
      });
  };

  const handleAddEcuType = () => {
    setAddEcuTypeModalOpen(true);
  };

  const handleSaveEcuType = (formData) => {
    const makeName = formData.make.trim();
    const ecuType = formData.ecuType.trim();
    APIManager.addEcuTypeDomainApprover(formData, makeName, ecuType)
      .then((response) => {
        fetchEcuTypes();
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setComponentName("appError");
        setErrorMessage(err.message);
        setHeaderMessage(
          err.message === "jwt expired" ? "401 Token Expired" : "Error",
        );
        setOpenErrorPopup(true);
      });
  };

  const uniqueMakes = [...new Set(ecuData.map((item) => item.make))].sort((a, b) => a.localeCompare(b));

  return (
    <>
      {loader && (
        <Backdrop
          open={loader}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {!loader && !error && (
        <div className="ecu-type-container">
      <div className="ecu-type-content">
        {/* Header Section */}
        <div className="ecu-type-header">
          <div className="ecu-type-header-left">
            <h2 className="ecu-type-title">ECU Types</h2>
          </div>
          <Button
            onClick={handleAddEcuType}
            variant="contained"
            sx={{
              height: "36px",
              minWidth: "117px",
              padding: "6px 10px",
              textTransform: "none",
              borderRadius: "4px",
              color: "#FFFFFF",
              backgroundColor: "#0476E2",
              fontSize: "13px",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#0356a6",
              },
            }}
            disabled={readOnly_DomainApprover}
          >
            Add ECU Type
          </Button>
        </div>

        {/* Loading State */}
        {/* Add ECU Type Modal */}
        <AddEcuTypeModal
          open={addEcuTypeModalOpen}
          onClose={() => setAddEcuTypeModalOpen(false)}
          onSave={handleSaveEcuType}
          existingMakes={uniqueMakes}
        />

        {/* Table */}
        <TableContainer className="ecu-type-table-container">
          <Table
            sx={{
              "& .MuiTableCell-root": {
                padding: "12px 16px",
                borderBottom: "1px solid #E0E0E0",
              },
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <TableRow
                sx={{
                  backgroundColor: "#E8E8E8",
                  height: "48px",
                }}
              >
                <TableCell
                  sx={{
                    width: "50%",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={handleSort}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#121415",
                      }}
                    >
                      ECU Type
                    </Typography>
                    {sortOrder === "asc" ? (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: "18px",
                          color: "#12141599",
                        }}
                      />
                    ) : (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: "18px",
                          color: "#12141599",
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ width: "50%" }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#121415",
                    }}
                  >
                    Make
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <div className="ecu-type-empty-state">
                      No ECU types found
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      height: "48px",
                      "&:hover": {
                        backgroundColor: "#F5F5F5",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#121415",
                        }}
                      >
                        {item.ecuType || "--"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <div className="ecu-type-make-cell">
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#121415",
                          }}
                        >
                          {item.make}
                        </Typography>
                        <div className="ecu-type-actions">  
                          <IconButton
                            size="small"
                            sx={{
                              padding: "4px",
                              color: "#666666",
                              "&:hover": {
                                backgroundColor: "#E0E0E0",
                              },
                            }}
                            onClick={() => handleDelete(item)}
                            disabled={readOnly_DomainApprover || item.ecuType === ""}
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="ecu-type-pagination">
            <span className="ecu-type-pagination-text">
              Page {currentPage + 1} of {totalPages}
            </span>
            <IconButton
              size="small"
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              sx={{
                color: currentPage === 0 ? "#CCCCCC" : "#666666",
                "&:hover": {
                  backgroundColor:
                    currentPage === 0 ? "transparent" : "#F5F5F5",
                },
              }}
            >
              <KeyboardDoubleArrowLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setCurrentPage((prev) => prev - 1)}
              disabled={currentPage === 0}
              sx={{
                color: currentPage === 0 ? "#CCCCCC" : "#666666",
                "&:hover": {
                  backgroundColor:
                    currentPage === 0 ? "transparent" : "#F5F5F5",
                },
              }}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={currentPage === totalPages - 1}
              sx={{
                color:
                  currentPage === totalPages - 1 ? "#CCCCCC" : "#666666",
                "&:hover": {
                  backgroundColor:
                    currentPage === totalPages - 1
                      ? "transparent"
                      : "#F5F5F5",
                },
              }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              sx={{
                color:
                  currentPage === totalPages - 1 ? "#CCCCCC" : "#666666",
                "&:hover": {
                  backgroundColor:
                    currentPage === totalPages - 1
                      ? "transparent"
                      : "#F5F5F5",
                },
              }}
            >
              <KeyboardDoubleArrowRightIcon />
            </IconButton>
          </div>
        )}
       
      </div>
    </div>
      )}

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
    </>
  );
};

export default EcuType;
